import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import { useTranslation } from 'react-i18next';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
}));

const TAG_MANAGEMENT_DIALOG_COPY = {
  de: {
    title: 'Tag-Verwaltung',
    description: 'Hier können Sie alle Tags verwalten, gruppieren und deren Verwendung sehen.',
    allTags: 'Alle Tags ({{count}})',
    columns: {
      name: 'Tag Name',
      category: 'Kategorie',
      visibility: 'Sichtbarkeit',
      createdBy: 'Erstellt von',
      usage: 'Verwendungen',
      actions: 'Aktionen',
    },
    private: 'Privat',
    public: 'Öffentlich',
    you: '(Sie)',
    publishPublic: 'Als öffentlich veröffentlichen',
    privateInfoTitle: '🔒 Private Tags:',
    privateInfoBody: 'Nur für Sie sichtbar. Können jederzeit von Ihnen gelöscht oder öffentlich gemacht werden.',
    publicInfoTitle: '🌐 Öffentliche Tags:',
    publicInfoBody: 'Für alle Nutzer sichtbar. Data Stewards können öffentliche Tags direkt entfernen.',
    close: 'Schließen',
  },
  en: {
    title: 'Tag management',
    description: 'Here you can manage all tags, group them, and review their usage.',
    allTags: 'All tags ({{count}})',
    columns: {
      name: 'Tag name',
      category: 'Category',
      visibility: 'Visibility',
      createdBy: 'Created by',
      usage: 'Uses',
      actions: 'Actions',
    },
    private: 'Private',
    public: 'Public',
    you: '(you)',
    publishPublic: 'Publish as public',
    privateInfoTitle: '🔒 Private tags:',
    privateInfoBody: 'Visible only to you. You can delete them at any time or make them public.',
    publicInfoTitle: '🌐 Public tags:',
    publicInfoBody: 'Visible to all users. Data stewards can remove public tags directly.',
    close: 'Close',
  },
  pt: {
    title: 'Gerenciamento de tags',
    description: 'Aqui você pode gerenciar todas as tags, agrupá-las e ver como são usadas.',
    allTags: 'Todas as tags ({{count}})',
    columns: {
      name: 'Nome da tag',
      category: 'Categoria',
      visibility: 'Visibilidade',
      createdBy: 'Criado por',
      usage: 'Usos',
      actions: 'Ações',
    },
    private: 'Privado',
    public: 'Público',
    you: '(você)',
    publishPublic: 'Publicar como público',
    privateInfoTitle: '🔒 Tags privadas:',
    privateInfoBody: 'Visíveis apenas para você. Você pode excluí-las a qualquer momento ou torná-las públicas.',
    publicInfoTitle: '🌐 Tags públicas:',
    publicInfoBody: 'Visíveis para todos os usuários. Os data stewards podem remover tags públicas diretamente.',
    close: 'Fechar',
  },
};

/**
 * Tag Management Dialog Component
 * Dialog for managing and viewing all tags
 *
 * @param {boolean} open - Whether dialog is open
 * @param {Function} onClose - Handler for closing dialog
 * @param {Array} allTagsWithStats - All tags with statistics
 * @param {string} currentUser - Current user name
 * @param {Function} getIconComponent - Get icon component by name
 * @param {Function} handleMakeTagPublic - Handler for making tag public
 */
const TagManagementDialog = ({
  open,
  onClose,
  allTagsWithStats,
  currentUser,
  getIconComponent,
  handleMakeTagPublic,
}) => {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage?.startsWith('pt')
    ? 'pt'
    : i18n.resolvedLanguage?.startsWith('en')
      ? 'en'
      : 'de';
  const copy = TAG_MANAGEMENT_DIALOG_COPY[language];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalOfferIcon />
          {copy.title}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" paragraph>
          {copy.description}
        </Typography>

        {/* Tag Statistics */}
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          {copy.allTags.replace('{{count}}', allTagsWithStats.length)}
        </Typography>

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <StyledTableCell>{copy.columns.name}</StyledTableCell>
                <StyledTableCell>{copy.columns.category}</StyledTableCell>
                <StyledTableCell>{copy.columns.visibility}</StyledTableCell>
                <StyledTableCell>{copy.columns.createdBy}</StyledTableCell>
                <StyledTableCell align="right">{copy.columns.usage}</StyledTableCell>
                <StyledTableCell>{copy.columns.actions}</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allTagsWithStats.map((tag) => {
                const isOwnTag = tag.createdBy === currentUser;
                const CustomIconComponent = tag.icon ? getIconComponent(tag.icon) : null;

                return (
                  <TableRow key={tag.name} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {CustomIconComponent ? (
                          <CustomIconComponent fontSize="small" color="primary" />
                        ) : tag.isPrivate ? (
                          <LockIcon fontSize="small" color="secondary" />
                        ) : (
                          <PublicIcon fontSize="small" color="primary" />
                        )}
                        {tag.name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={tag.category} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tag.isPrivate ? copy.private : copy.public}
                        size="small"
                        color={tag.isPrivate ? 'secondary' : 'primary'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color={isOwnTag ? 'primary' : 'textSecondary'}>
                        {tag.createdBy}
                        {isOwnTag && ` ${copy.you}`}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={tag.count} size="small" color="info" />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {tag.isPrivate && isOwnTag && (
                          <Tooltip title={copy.publishPublic}>
                            <IconButton
                              size="small"
                              onClick={() => handleMakeTagPublic(tag.name)}
                            >
                              <PublicIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            <strong>{copy.privateInfoTitle}</strong> {copy.privateInfoBody}
          </Typography>
          <Typography variant="body2">
            <strong>{copy.publicInfoTitle}</strong> {copy.publicInfoBody}
          </Typography>
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{copy.close}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TagManagementDialog;
